import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Seo from '@/components/seo/Seo';
import { submitContact } from '@/lib/contactApi';
import type { CorporateBrochureFormData } from '../components/BrochureForm';

const FIELD_ORDER: (keyof CorporateBrochureFormData)[] = [
  'company', 'department', 'name', 'email', 'phone', 'industry',
  'format', 'participants', 'timeline', 'message',
];

export default function CorporateBrochureConfirmPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const formData = (location.state as { formData?: CorporateBrochureFormData })?.formData;
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!formData) {
      navigate('/corporate/brochure', { replace: true });
    }
  }, [formData, navigate]);

  if (!formData) return null;

  const industryLabel: Record<string, string> = {
    hotel: t('corporateBrochure.form.industry.hotel'),
    residence: t('corporateBrochure.form.industry.residence'),
    finance: t('corporateBrochure.form.industry.finance'),
    retail: t('corporateBrochure.form.industry.retail'),
    'luxury-sales': t('corporateBrochure.form.industry.luxurySales'),
    clinic: t('corporateBrochure.form.industry.clinic'),
    automotive: t('corporateBrochure.form.industry.automotive'),
    chauffeur: t('corporateBrochure.form.industry.chauffeur'),
    other: t('corporateBrochure.form.industry.other'),
  };

  const formatLabel: Record<string, string> = {
    dispatch: t('corporateBrochure.form.format.dispatch'),
    onsite: t('corporateBrochure.form.format.onsite'),
    undecided: t('corporateBrochure.form.format.undecided'),
  };

  const formatValue = (key: keyof CorporateBrochureFormData, value: string): string => {
    if (!value) return t('corporateBrochure.confirm.unspecified');
    if (key === 'industry') return industryLabel[value] || value;
    if (key === 'format') return formatLabel[value] || value;
    return value;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');

    const payload: Record<string, string> = {};
    FIELD_ORDER.forEach((key) => {
      payload[key] = formData[key] as string;
    });
    payload.privacyAgreed = formData.privacyAgreed ? '同意' : '未同意';

    try {
      const resp = await submitContact('corporate-brochure', payload);

      if (resp.ok) {
        navigate('/corporate/brochure/complete', { replace: true });
      } else {
        setSubmitError(t('corporateBrochure.confirm.error'));
      }
    } catch {
      setSubmitError(t('corporateBrochure.confirm.networkError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo route="/corporate/brochure/confirm" />
      <Header />
      <main className="pt-[120px] pb-20">
        <div className="max-w-[700px] mx-auto px-8">
          <div className="flex items-center gap-2 text-[13px] tracking-[0.06em] mb-10 text-foreground-400">
            <Link to="/" className="hover:text-accent-500 transition-colors">{t('corporateBrochure.confirm.homeLink')}</Link>
            <span>&gt;</span>
            <Link to="/corporate" className="hover:text-accent-500 transition-colors">{t('corporateBrochure.confirm.corporateLink')}</Link>
            <span>&gt;</span>
            <Link to="/corporate/brochure" className="hover:text-accent-500 transition-colors">{t('corporateBrochure.confirm.brochureLink')}</Link>
            <span>&gt;</span>
            <span className="text-accent-600">{t('corporateBrochure.confirm.currentPage')}</span>
          </div>

          <div className="text-center mb-12">
            <h1 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-4">
              {t('corporateBrochure.confirm.title')}
            </h1>
            <div className="gold-line mx-auto mb-4"></div>
            {String(t('corporateBrochure.confirm.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[16px] text-foreground-600 tracking-[0.04em]">{__s}</p>))}
          </div>

          <div className="border border-background-200/80 bg-background-100/50 p-8 mb-8">
            <div className="space-y-5">
              {FIELD_ORDER.map((key) => (
                <div key={key} className="border-b border-background-200/40 pb-4 last:border-b-0 last:pb-0">
                  <p className="text-[13px] text-foreground-500 tracking-[0.08em] mb-1 font-label">
                    {t(`corporateBrochure.confirm.field.${key}`)}
                  </p>
                  <p className="text-[16px] text-foreground-800 tracking-[0.03em] leading-relaxed">
                    {formatValue(key, formData[key] as string)}
                  </p>
                </div>
              ))}
              <div className="border-b border-background-200/40 pb-4 last:border-b-0 last:pb-0">
                {String(t('corporateBrochure.confirm.field.privacy')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-500 tracking-[0.08em] mb-1 font-label">{__s}</p>))}
                {String(t('corporateBrochure.confirm.agreed')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[16px] text-foreground-800 tracking-[0.03em] leading-relaxed">{__s}</p>))}
              </div>
            </div>
          </div>

          {submitError && (
            <p className="text-[15px] text-red-500 text-center mb-6 tracking-[0.03em] leading-relaxed">{submitError}</p>
          )}

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate('/corporate/brochure', { state: { formData } })}
              className="px-8 py-3 border border-background-300 text-foreground-600 hover:text-foreground-800 hover:border-foreground-400 text-[15px] tracking-[0.08em] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('corporateBrochure.confirm.edit')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-glow px-10 py-3 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {submitting ? t('corporateBrochure.confirm.submitting') : t('corporateBrochure.confirm.submit')}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
